import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box,
} from '@mui/material'
import { ReportingOrder } from 'api/orderManagement/types'
import React, { useState, useEffect, useMemo } from 'react'

interface RefundModalProps {
  isOpen: boolean
  onClose: (_event: Record<string, never>, _reason: 'backdropClick' | 'escapeKeyDown') => void
  onSubmit: (isFullRefund: boolean, refundAmount?: number) => Promise<any>
  order: ReportingOrder | null
}

const RefundModal: React.FC<RefundModalProps> = ({ isOpen, onClose, onSubmit, order }) => {
  const [refundType, setRefundType] = useState<'full' | 'partial'>('full')
  const [refundAmount, setRefundAmount] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Calculate order total from order data
  const orderTotal = useMemo(() => {
    if (!order) return 0;
    
    const subtotal = parseFloat(order.attributes.subtotal || '0');
    const tax = parseFloat(order.attributes.tax || '0');
    const tip = parseFloat(order.attributes.tip || '0');
    const deliveryFee = parseFloat(order.attributes.delivery_fee || '0');
    
    return subtotal + tax + tip + deliveryFee;
  }, [order]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setRefundType('full')
      setRefundAmount('')
      setError('')
      setIsSubmitting(false)
    }
  }, [isOpen])

  const handleRefundTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRefundType(event.target.value as 'full' | 'partial')
    setError('')
  }

  const handleRefundAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    
    // Allow only valid currency input (digits and one decimal point)
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setRefundAmount(value)
      
      // Validate amount
      const numValue = parseFloat(value)
      if (value && (isNaN(numValue) || numValue <= 0)) {
        setError('Please enter a valid amount greater than $0.00')
      } else if (numValue > orderTotal) {
        setError(`Amount cannot exceed order total: $${orderTotal.toFixed(2)}`)
      } else {
        setError('')
      }
    }
  }

  const handleClose = () => {
    onClose({} as Record<string, never>, 'backdropClick')
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      
      if (refundType === 'partial') {
        const amount = parseFloat(refundAmount)
        if (isNaN(amount) || amount <= 0) {
          setError('Please enter a valid amount greater than $0.00')
          setIsSubmitting(false)
          return
        }
        if (amount > orderTotal) {
          setError(`Amount cannot exceed order total: $${orderTotal.toFixed(2)}`)
          setIsSubmitting(false)
          return
        }
        await onSubmit(false, amount)
      } else {
        await onSubmit(true)
      }
      
      handleClose()
    } catch (error) {
      console.error('Error processing refund:', error)
      setIsSubmitting(false)
    }
  }

  const isSubmitDisabled = 
    isSubmitting || 
    (refundType === 'partial' && (refundAmount === '' || parseFloat(refundAmount) <= 0 || parseFloat(refundAmount) > orderTotal))

  if (!order) return null

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: '16px',
        },
      }}
    >
      <DialogTitle>
        <Typography variant='h6' sx={{ fontWeight: 600 }}>
          Are you sure you want to refund this order?
        </Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
          Once refunded, this action cannot be undone.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            aria-label="refund-type"
            name="refund-type"
            value={refundType}
            onChange={handleRefundTypeChange}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <FormControlLabel 
                value="full" 
                control={<Radio />} 
                label="Full Refund" 
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel 
                value="partial" 
                control={<Radio />} 
                label="Partial Refund" 
              />
              
              {refundType === 'partial' && (
                <TextField
                  variant="outlined"
                  size="small"
                  value={refundAmount}
                  onChange={handleRefundAmountChange}
                  placeholder="0.00"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{ 
                    ml: 2, 
                    width: '120px',
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: error ? 'error.main' : 'primary.main',
                        borderWidth: '2px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: error ? 'error.main' : 'primary.main',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: error ? 'error.main' : undefined,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        boxShadow: 'none',
                        outline: 'none',
                      }
                    }
                  }}
                  autoFocus
                />
              )}
            </Box>
          </RadioGroup>
        </FormControl>
        
        {error && (
          <Typography 
            variant="caption" 
            color="error" 
            sx={{ 
              display: 'block', 
              mt: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '16px', gap: '8px' }}>
        <Button
          variant='outlined'
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: 'grey.300',
            '&:hover': {
              borderColor: 'grey.400',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          Refund
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RefundModal 